// src/new/components/monitor/TriageChat/TriageChatProvider.tsx
//
// Single app-level host for the conversational triage modal. Any surface
// (flow-run rows, flow DAG nodes, the Resource Triage page, Notifications, the
// Flows listing, deep links) opens triage via `useTriageLauncher().open(ctx)`.
// Owning ONE modal above all page renders means the chat + its WebSocket are
// never torn down by a page re-render/unmount — which is the class of bug that
// previously broke follow-up context.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import { API_VEDA_AI_URL, API_EXPRESS_CODE_URL } from 'js/constants/api';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { useGrowthBookContext } from 'new/contexts/GrowthBookProvider';
import { TriageChatModal } from './TriageChatModal';
import { TriageContext } from './triageContext';

/** Where an open() call originated — lets us measure each entry point's funnel
 *  separately in PostHog (e.g. the notification deep link vs. the row icon). */
export type TriageOpenSource =
  | 'notification_deeplink'
  | 'notification_panel'
  | 'flow_run_row'
  | 'flow_dag_node'
  | 'flows_listing'
  | 'resource_triage_page'
  | 'unknown';

/** Sources that keep today's auto-seed-and-go behavior: the agent already
 *  knows what the user is here about (a specific failure the link/notification
 *  named), so the conversation starts itself. Every other source opens on the
 *  suggested actions empty state instead — see the triage suggested-actions
 *  design spec (plans/2026-07-28-triage-suggested-actions-design.md).
 *  `notification_panel` carries the same concrete `errorSummary` as
 *  `notification_deeplink` (see NotificationTriageButton), so it meets the
 *  same condition and gets the same treatment. */
const AUTO_SEED_SOURCES: TriageOpenSource[] = ['notification_deeplink', 'notification_panel'];

interface TriageLauncher {
  /** True when the triage feature is enabled for this org (GrowthBook). Use to
   *  decide whether to render an entry-point button at all. */
  enabled: boolean;
  /** Open the triage modal with the given context. No-op when disabled (the
   *  no-op is tracked as a flag-off degradation). `source` attributes the open
   *  to an entry point for funnel analysis. */
  open: (context: TriageContext | null, source?: TriageOpenSource) => void;
  close: () => void;
}

const TriageChatContext = createContext<TriageLauncher>({
  enabled: false,
  open: () => {},
  close: () => {},
});

export const useTriageLauncher = (): TriageLauncher => useContext(TriageChatContext);

export const TriageChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Triage requires BOTH the org flag AND a configured backend URL for the
  // ACTIVE backend. Gate on the same backend the container will select
  // (UI_TRIAGE_EXPRESS_BACKEND → express-code URL, else veda-ai URL) so an
  // express-only deployment (veda URL unset) still shows every entry point +
  // the modal. If the active backend's URL is blank there is nothing to talk
  // to, so every entry point stays hidden — exactly as if the flag were off.
  // Entry-point buttons read `enabled`, so gating here disables them all in
  // one place.
  const flagOn = useFeatureFlag('UI_AI_TRIAGE_CHAT');
  const useExpressBackend = useFeatureFlag('UI_TRIAGE_EXPRESS_BACKEND');
  const backendUrl = useExpressBackend ? API_EXPRESS_CODE_URL : API_VEDA_AI_URL;
  const enabled = flagOn && !!backendUrl;
  // `isReady` lets us tell "flag still loading" from "flag resolved-off" — needed
  // so a deep link that lands before GrowthBook resolves isn't dropped, and so we
  // don't mis-report a still-loading flag as a flag-off degradation.
  const { isReady } = useGrowthBookContext();
  const [context, setContext] = useState<TriageContext | null>(null);
  // State (not a ref) so the derived `autoSeed` below is reactive and reaches
  // the modal on the same render that opens it.
  const [openSource, setOpenSource] = useState<TriageOpenSource>('unknown');
  const autoSeed = AUTO_SEED_SOURCES.includes(openSource);
  // Guards the one-shot funnel analytics for the current context (re-armed on
  // each open / close).
  const analyticsDoneRef = useRef(false);

  const open = useCallback((ctx: TriageContext | null, source: TriageOpenSource = 'unknown') => {
    if (!ctx) {
      AnalyticsUtils.captureCustomEvent('triage_modal_blocked', { source, reason: 'no_context' });
      return;
    }
    setOpenSource(source);
    analyticsDoneRef.current = false;
    // Store the context regardless of the flag's load state. The modal only
    // RENDERS when `enabled` (below), so a deep link that lands before GrowthBook
    // resolves still opens once the flag turns on — instead of being silently
    // dropped by a not-yet-ready flag (which broke cold-load deep links).
    setContext(ctx);
  }, []);

  const close = useCallback(() => {
    setContext(null);
    analyticsDoneRef.current = false;
  }, []);

  // Funnel "modal" analytics, fired once GrowthBook has resolved for the pending
  // context: `opened` when the flag is on (the modal shows), `blocked/flag_off`
  // when it's resolved-off. While the flag is still loading, we wait.
  useEffect(() => {
    if (!context || analyticsDoneRef.current) return;
    if (enabled) {
      analyticsDoneRef.current = true;
      AnalyticsUtils.captureCustomEvent('triage_modal_opened', {
        source: openSource,
        resource_type: context.resourceType,
        resource_id: context.resourceId,
        has_run_id: !!context.runId,
        has_error_message: !!context.errorSummary,
      });
    } else if (isReady) {
      analyticsDoneRef.current = true;
      AnalyticsUtils.captureCustomEvent('triage_modal_blocked', {
        source: openSource,
        reason: 'flag_off',
        resource_type: context.resourceType,
      });
    }
  }, [enabled, isReady, context, openSource]);

  // Deep-link / notification entry is handled by the dedicated `/monitor/triage`
  // route (TriageLinkLanding), which calls `open(ctx)` after redirecting to the
  // Integrate canvas — so there's no URL-param scanning here.

  return (
    <TriageChatContext.Provider value={{ enabled, open, close }}>
      {children}
      {enabled && (
        <TriageChatModal open={!!context} context={context} onClose={close} autoSeed={autoSeed} />
      )}
    </TriageChatContext.Provider>
  );
};
